import { FullscreenModal } from '@xo-union/tk-component-modals';
import Alert from '@xo-union/tk-component-alerts';
import isEmpty from 'lodash/isEmpty';
import React, { useCallback, useContext, useEffect, useReducer, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { compose } from '@xo-union/react-css-modules';

import {
  useCreatePurchaseDomainMutation,
  useSearchDomainQuery,
  WeddingWebsiteQuery,
  WeddingWebsiteQueryDocument,
  Wws_SearchDomain,
} from '../../generated/graphql';
import styles from './index.scss';
import MatchedDomain from './MatchedDomain';
import PaymentForm from './PaymentForm';
import Search from './Search';
import SelectDomain from './SelectDomain';
import Step, { StepProps, STEPS } from './Step';
import { TrackingContext } from '../../TrackingContext';
import { SnackbarMessage, SNACKBAR_DOMAIN_PAYMENT_TYPE } from '../Snackbar';

interface Props {
  onClose?: () => void;
  setDomainPaymentToastMessage: (message: SnackbarMessage) => void;
}

interface State {
  domain: string;
  searchTerm?: string | null;
  searchResults: Wws_SearchDomain[];
  selectedDomain?: Wws_SearchDomain;
  step: number;
  tld: string;
}

interface Payload {
  text?: string;
  domains?: Wws_SearchDomain[];
}

interface Action {
  type: string;
  payload?: Payload;
}

const initialState: State = {
  domain: '',
  searchTerm: '',
  searchResults: [],
  step: STEPS.search,
  tld: '',
};

const extractSelectedDomain = (domain: string, domainList: Wws_SearchDomain[]) =>
  domainList.find((res: Wws_SearchDomain) => res.name === domain);

const reducer = (state: State, { type, payload }: Action): State => {
  switch (type) {
    case 'setSearchTerm':
      const domainParts = (payload?.text || '').split('.');
      let tld = domainParts.pop();
      return {
        ...state,
        domain: domainParts.join(''),
        searchTerm: payload?.text || '',
        tld: tld || '',
      };
    case 'setSearchResults':
      const selectedDomain = extractSelectedDomain(state.searchTerm || '', payload?.domains || []);
      return {
        ...state,
        searchResults: payload?.domains || [],
        selectedDomain,
        step: isEmpty(payload?.domains) ? STEPS.search : STEPS.selectOption,
      };
    case 'reset':
      return initialState;
    case 'setDomain':
      return {
        ...state,
        selectedDomain: extractSelectedDomain(payload?.text || '', state.searchResults),
        step: STEPS.buy,
      };
    case 'confirm':
      return {
        ...state,
        step: STEPS.buy,
      };
    default:
      throw new Error();
  }
};

const DEFAULT_ERROR_MESSAGE =
  "We're currently experiencing a system issue. Please refresh your browser and check your account to see if your transaction was successful before trying again. If you need help, please contact Customer Support.";

const PurchaseDomainModal: React.FC<Props> = ({ setDomainPaymentToastMessage, onClose }) => {
  const [{ searchTerm, searchResults, step, domain, selectedDomain, tld }, dispatch] = useReducer(
    reducer,
    initialState
  );
  const history = useHistory();
  const [create, { loading: buying }] = useCreatePurchaseDomainMutation();
  const { track } = useContext(TrackingContext);

  const { loading, data } = useSearchDomainQuery({
    skip: isEmpty(searchTerm),
    variables: {
      domainName: domain || '',
    },
  });

  const onBuy = async (nonce: string, registrationLength: number) => {
    try {
      await create({
        variables: {
          attributes: {
            paymentMethodNonce: nonce,
            domainName: selectedDomain?.name || '',
            years: registrationLength,
          },
        },
        update(cache, { data }) {
          const { weddingWebsite, ...rest } = cache.readQuery<WeddingWebsiteQuery>({
            query: WeddingWebsiteQueryDocument,
          })!;
          const updatedWeddingWebsite = { ...weddingWebsite, purchaseDomain: data?.createPurchaseDomain };
          cache.writeQuery({
            query: WeddingWebsiteQueryDocument,
            data: { ...rest, weddingWebsite: updatedWeddingWebsite },
          });
        },
      });
      track('Custom WWS Domain Purchased', {
        registration_length: registrationLength,
        price: registrationLength * (selectedDomain?.price || 0),
      });
      setDomainPaymentToastMessage({
        message: 'You’re all set! Your domain will be applied within a few hours.',
        type: SNACKBAR_DOMAIN_PAYMENT_TYPE,
      });
      if (onClose !== undefined) {
        onClose();
      } else {
        history.push('/gs/dashboard');
      }
    } catch (error) {
      // TODO honeybadger & reporting more details to the user when appropriate - TKGUEST-3013
      showAndDismissAlert(DEFAULT_ERROR_MESSAGE);
    }
  };

  useEffect(() => {
    dispatch({
      type: 'setSearchResults',
      payload: {
        domains: data?.searchDomains?.filter(sd => sd.available && !sd.isPremiumName) || [],
      },
    });
  }, [data]);

  const alertRef = useRef<any>(null);
  const showAndDismissAlert = useCallback((message: string) => {
    alertRef.current.showAndDismiss({
      type: 'error',
      content: message,
      waitTime: 10000,
    });
  }, []);

  const searchDomain = (domainName: string) => {
    track('Custom WWS Domain Searched', { custom_domain_name: domainName });
    dispatch({
      type: 'setSearchTerm',
      payload: {
        text: domainName,
      },
    });
  };

  const modalClasses = compose({
    'header-section': styles['header-section'],
    'header-section-center': styles['header-section-center'],
    'header-section-right': styles['header-section-right'],
    'tk-logo': styles['tk-logo'],
    container: styles['container'],
  });

  return (
    <div className={styles.modalWrapper}>
      <FullscreenModal
        onClose={() => (onClose === undefined ? history.push('/gs/dashboard') : onClose())}
        contentClassName={styles.modalContent}
        classes={modalClasses}
      >
        <Step
          currentStep={STEPS[step] as keyof StepProps}
          search={
            <Search
              onSubmit={domain => {
                searchDomain(domain);
              }}
              loading={loading}
              noMatch={!!searchTerm}
            />
          }
          match={
            <MatchedDomain
              domainName={domain}
              tld={tld}
              onCancel={() => dispatch({ type: 'reset' })}
              onConfirm={() => {
                track('Custom WWS Domain Name Confirmation');
                dispatch({ type: 'confirm' });
              }}
            />
          }
          selectOption={
            <SelectDomain
              domainName={searchTerm || ''}
              domains={searchResults}
              onCancel={() => dispatch({ type: 'reset' })}
              onSelect={domain => dispatch({ type: 'setDomain', payload: { text: domain } })}
            />
          }
          buy={
            selectedDomain && (
              <PaymentForm
                domainName={selectedDomain?.name || ''}
                domainPrice={selectedDomain?.price}
                isPremiumName={selectedDomain?.isPremiumName}
                isInRedemptionPeriod={false}
                primaryText="Place order"
                secondaryText="Start over"
                onCancel={() => dispatch({ type: 'reset' })}
                onBuy={onBuy}
                loading={buying}
              />
            )
          }
        />
        <Alert ref={alertRef} position="top" />
      </FullscreenModal>
    </div>
  );
};

export default PurchaseDomainModal;
